import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  organization: "",
  phone: "",
  email: "",
  num_shirts: "",
  order_type: "Premium Uniform",
  deadline: "",
  message: "",
};

/**
 * Compact lead form — used on SEO landing pages.
 * Posts to the same /api/leads endpoint as the main contact form.
 */
export default function LeadFormCompact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Please add an email or phone so we can reply");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, email: form.email.trim() || undefined };
      await axios.post(`${API}/leads`, payload);
      toast.success("Got it! We'll be in touch within 24 hours.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail[0]?.msg || "Validation error"
        : detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="compact-lead-form"
      className="bg-zinc-900 border border-zinc-800 p-7 md:p-9"
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Full Name *" htmlFor="c-name">
          <Input
            id="c-name"
            data-testid="c-field-name"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Your name"
            className="bg-zinc-950 border-zinc-800 focus:border-teal-400 rounded-none h-12 text-white"
          />
        </Field>
        <Field label="Team / Organization" htmlFor="c-org">
          <Input
            id="c-org"
            data-testid="c-field-org"
            value={form.organization}
            onChange={set("organization")}
            placeholder="e.g. Auckland United Rugby"
            className="bg-zinc-950 border-zinc-800 focus:border-teal-400 rounded-none h-12 text-white"
          />
        </Field>
        <Field label="Phone" htmlFor="c-phone">
          <Input
            id="c-phone"
            data-testid="c-field-phone"
            value={form.phone}
            onChange={set("phone")}
            placeholder="+64 ..."
            className="bg-zinc-950 border-zinc-800 focus:border-teal-400 rounded-none h-12 text-white"
          />
        </Field>
        <Field label="Email" htmlFor="c-email">
          <Input
            id="c-email"
            type="email"
            data-testid="c-field-email"
            value={form.email}
            onChange={set("email")}
            placeholder="you@example.com"
            className="bg-zinc-950 border-zinc-800 focus:border-teal-400 rounded-none h-12 text-white"
          />
        </Field>
        <Field label="Quantity" htmlFor="c-qty">
          <Input
            id="c-qty"
            data-testid="c-field-qty"
            value={form.num_shirts}
            onChange={set("num_shirts")}
            placeholder="e.g. 25"
            className="bg-zinc-950 border-zinc-800 focus:border-teal-400 rounded-none h-12 text-white"
          />
        </Field>
        <Field label="Order Type" htmlFor="c-type">
          <Select value={form.order_type} onValueChange={set("order_type")}>
            <SelectTrigger
              id="c-type"
              data-testid="c-field-type"
              className="bg-zinc-950 border-zinc-800 focus:border-teal-400 rounded-none h-12 text-white"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none">
              <SelectItem value="Premium Uniform">Premium Uniform</SelectItem>
              <SelectItem value="Fast Print">Fast Print (DTF)</SelectItem>
              <SelectItem value="Not Sure">Not sure yet</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Deadline" htmlFor="c-deadline" className="sm:col-span-2">
          <Input
            id="c-deadline"
            data-testid="c-field-deadline"
            value={form.deadline}
            onChange={set("deadline")}
            placeholder="e.g. Need by 15 March, ASAP, 4 weeks..."
            className="bg-zinc-950 border-zinc-800 focus:border-teal-400 rounded-none h-12 text-white"
          />
        </Field>
        <Field label="Message" htmlFor="c-msg" className="sm:col-span-2">
          <Textarea
            id="c-msg"
            data-testid="c-field-message"
            value={form.message}
            onChange={set("message")}
            rows={4}
            placeholder="Tell us about your team, vibe, colours, any inspiration..."
            className="bg-zinc-950 border-zinc-800 focus:border-teal-400 rounded-none text-white resize-none"
          />
        </Field>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="c-submit"
        className="btn-primary mt-7 inline-flex items-center gap-2 disabled:opacity-60"
      >
        {submitting ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Sending...
          </>
        ) : (
          <>
            Send Enquiry
            <Send size={18} />
          </>
        )}
      </button>
    </form>
  );
}

function Field({ label, htmlFor, className = "", children }) {
  return (
    <div className={className}>
      <Label
        htmlFor={htmlFor}
        className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 font-semibold"
      >
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
